import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Products = () => {
  const products = [
    {
      title: 'Fertiliser (Potash MOP)',
      category: 'Agricultural Solutions',
      description: 'High-grade Muriate of Potash (MOP) fertiliser, essential for agricultural productivity and crop yield optimization.',
      features: [
        'Premium quality potash from reliable sources',
        'Consistent K₂O content (60-62%)',
        'Various packaging options available',
        'Bulk and containerized shipments',
        'Complete documentation and certification',
      ],
      applications: [
        'Agricultural crop production',
        'Soil nutrient enhancement',
        'Specialty fertilizer manufacturing',
        'Export to agricultural markets',
      ],
      regions: ['Europe', 'Asia', 'Africa'],
      image: '🌱',
    },
    {
      title: 'Thermal Coal',
      category: 'Energy Solutions',
      description: 'Quality thermal coal for power generation and industrial applications, sourced from certified suppliers worldwide.',
      features: [
        'Various calorific values (CV) available',
        'Low ash and sulfur content options',
        'Compliant with international standards',
        'Reliable supply chain logistics',
        'Quality assurance and testing',
      ],
      applications: [
        'Power plant fuel',
        'Industrial heating',
        'Cement manufacturing',
        'Steel production support',
      ],
      regions: ['Europe', 'Asia'],
      image: '⚡',
    },
    {
      title: 'Wheat',
      category: 'Agricultural Commodities',
      description: 'Premium quality wheat varieties for milling, food production, and animal feed applications.',
      features: [
        'Multiple wheat grades available',
        'Milling wheat and feed wheat',
        'Quality control and testing',
        'Phytosanitary certification',
        'Flexible delivery schedules',
      ],
      applications: [
        'Flour milling',
        'Food manufacturing',
        'Animal feed production',
        'Export markets',
      ],
      regions: ['Europe', 'Asia', 'Africa'],
      image: '🌾',
    },
    {
      title: 'Metals (Titanium)',
      category: 'Industrial Materials',
      description: 'Specialized metals supply including titanium and other industrial metals for manufacturing and aerospace sectors.',
      features: [
        'Various grades and forms',
        'Certified material quality',
        'Technical specifications support',
        'Customized sourcing available',
        'Complete traceability',
      ],
      applications: [
        'Aerospace components',
        'Industrial manufacturing',
        'Chemical processing',
        'Medical equipment',
      ],
      regions: ['Europe', 'Asia'],
      image: '⚙️',
    },
  ];

  const additionalServices = [
    {
      icon: '🚢',
      title: 'Logistics Support',
      description: 'End-to-end logistics management including shipping, customs clearance, and delivery coordination.',
    },
    {
      icon: '📋',
      title: 'Quality Assurance',
      description: 'Comprehensive quality control, testing, and certification to meet international standards.',
    },
    {
      icon: '🔍',
      title: 'Custom Sourcing',
      description: 'Ability to source and supply other commodities based on specific client requirements.',
    },
    {
      icon: '💼',
      title: 'Flexible Terms',
      description: 'Competitive pricing and flexible payment terms tailored to your business needs.',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-accent text-white pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Products We Handle
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Comprehensive commodity trading solutions across multiple sectors and regions
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } gap-8 items-center bg-white rounded-2xl shadow-xl p-8 lg:p-12`}
              >
                {/* Image/Icon Side */}
                <div className="lg:w-1/3">
                  <div className="text-8xl text-center lg:text-left">
                    {product.image}
                  </div>
                  <div className="mt-6 flex flex-wrap gap-2 justify-center lg:justify-start">
                    {product.regions.map((region, idx) => (
                      <span
                        key={idx}
                        className="bg-secondary text-primary px-3 py-1 rounded-full text-sm font-semibold"
                      >
                        {region}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Content Side */}
                <div className="lg:w-2/3">
                  <span className="text-secondary font-semibold text-sm uppercase tracking-wide">
                    {product.category}
                  </span>
                  <h3 className="text-3xl font-bold text-primary mt-2 mb-4">
                    {product.title}
                  </h3>
                  <p className="text-gray-700 mb-6 text-lg">
                    {product.description}
                  </p>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Features */}
                    <div>
                      <h4 className="font-bold text-primary mb-3 flex items-center">
                        <span className="text-secondary mr-2">✓</span> Key Features
                      </h4>
                      <ul className="space-y-2">
                        {product.features.map((feature, idx) => (
                          <li key={idx} className="text-gray-700 text-sm flex items-start">
                            <span className="text-secondary mr-2 mt-1">•</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Applications */}
                    <div>
                      <h4 className="font-bold text-primary mb-3 flex items-center">
                        <span className="text-secondary mr-2">◆</span> Applications
                      </h4>
                      <ul className="space-y-2">
                        {product.applications.map((application, idx) => (
                          <li key={idx} className="text-gray-700 text-sm flex items-start">
                            <span className="text-secondary mr-2 mt-1">•</span>
                            <span>{application}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-primary mb-4">
              Additional Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive support beyond commodity supply
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition duration-300"
              >
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold text-primary mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-700">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-20 bg-gradient-to-r from-primary to-accent text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold mb-6">Quality & Compliance</h2>
            <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
              All our commodities meet international quality standards and come with complete documentation and certification
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="font-bold text-lg mb-2">Quality Testing</h3>
                <p className="text-gray-200">Independent laboratory analysis and verification</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="font-bold text-lg mb-2">Certification</h3>
                <p className="text-gray-200">Complete documentation and compliance certificates</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="font-bold text-lg mb-2">Traceability</h3>
                <p className="text-gray-200">Full supply chain transparency and tracking</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <h2 className="text-4xl font-bold text-primary mb-6">
            Interested in Our Products?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Contact us to discuss your specific requirements and get a quote
          </p>
          <Link
            to="/contact"
            className="inline-block bg-secondary hover:bg-secondary-dark text-primary font-bold px-12 py-4 rounded-lg transition duration-300 transform hover:scale-105 shadow-lg"
          >
            Request a Quote
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default Products;
